import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import { Gift, Loader2, RefreshCw, TrendingUp, Users } from "lucide-react";

/**
 * V185 — Admin analytics panel for the /vs/<slug> switcher funnel.
 * Lives inside the AdminPulseAITab grouping by default.
 */
export default function AdminSwitcherAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [err, setErr] = useState("");

  const adminToken = (typeof window !== "undefined" && localStorage.getItem("admin_token")) || "";

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await axios.get(`${API}/switcher-coupon/analytics`, {
        params: { days },
        headers: { "X-Admin-Token": adminToken },
      });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "load_failed");
    } finally {
      setLoading(false);
    }
  }, [days, adminToken]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="admin-switcher-analytics">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-emerald-600 font-extrabold mb-1">
            <Gift className="w-3 h-3" /> Switcher Funnel · V185
          </div>
          <h3 className="text-base font-bold text-gray-900">/vs/&lt;competitor&gt; auto-coupon performance</h3>
          <p className="text-sm text-gray-600 mt-1">
            Tracks which competitor comparison page brings the highest
            paying-customer switch-over rate.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-bold"
            data-testid="switcher-analytics-range"
          >
            {[7, 14, 30, 90].map(d => <option key={d} value={d}>last {d}d</option>)}
          </select>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-gray-900 text-white hover:bg-black"
            data-testid="switcher-analytics-refresh"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Refresh
          </button>
        </div>
      </div>

      {err && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3" data-testid="switcher-analytics-err">
          {err} {err === "admin_token_required" && "(localStorage.admin_token missing)"}
        </div>
      )}

      {data && (
        <>
          {/* Totals */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Stat icon={Users} label="Total visits" value={data.total_visits ?? 0} color="text-violet-600" />
            <Stat icon={Gift} label="Redemptions" value={data.total_redemptions ?? 0} color="text-emerald-600" />
            <Stat icon={TrendingUp} label="Overall CVR" value={`${data.overall_cvr_pct ?? 0}%`} color="text-fuchsia-600" />
          </div>

          {/* Per-slug table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="switcher-analytics-table">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-gray-500">Competitor</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-gray-500">Code</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-gray-500">Discount</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-gray-500">Visits</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-gray-500">Unique days</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-gray-500">Redemptions</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-gray-500">CVR</th>
                </tr>
              </thead>
              <tbody>
                {(data.per_slug || []).map(r => (
                  <tr key={r.slug} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50" data-testid={`switcher-row-${r.slug}`}>
                    <td className="px-3 py-2 font-bold text-gray-900 capitalize">{r.slug}</td>
                    <td className="px-3 py-2 font-mono text-xs text-violet-700">{r.code}</td>
                    <td className="px-3 py-2 text-right font-bold text-emerald-700">{r.discount_pct}%</td>
                    <td className="px-3 py-2 text-right">{r.visits}</td>
                    <td className="px-3 py-2 text-right">{r.unique_visitor_days}</td>
                    <td className="px-3 py-2 text-right font-bold">{r.redemptions}</td>
                    <td className={`px-3 py-2 text-right font-bold ${r.conversion_pct >= 3 ? "text-emerald-700" : r.conversion_pct > 0 ? "text-amber-600" : "text-gray-400"}`}>
                      {r.conversion_pct}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-gray-500 font-bold">
        <Icon className={`w-3 h-3 ${color}`} /> {label}
      </div>
      <div className="text-2xl font-black text-gray-900 mt-1">{value}</div>
    </div>
  );
}
